import clsx from "clsx";
import { FaRegEdit } from "react-icons/fa";
import { BsDownload, BsEye } from "react-icons/bs";
import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import {
  List,
  message,
  Modal,
  Select,
  Skeleton,
  Space,
  Typography,
} from "antd";
import { Darf } from "../Darf";
import apis from "../../services/apis";
import { DarfBolsa } from "../DarfBolsa";
import Button from "../../components/Button";
import { Container, Content } from "./styles";
import { Page } from "../../constants/brokers";
import { DarfExterior } from "../DarfExterior";
import { Rendimentos } from "../Rendimentos/index";
import { useBroker } from "../../contexts/BrokerContext";
import {
  download,
  errorMessage,
  formatCurrency,
  getInitialYear,
  monthsExtended,
} from "../../utils";
import {
  YearResumeStatusCSSClassEnum,
  YearResumeStatusTextEnum,
} from "../../constants/darf";

interface HistoricProps {
  item: Page;
}

export const Historic: React.FC<HistoricProps> = ({ item }) => {
  const { state } = useLocation();
  const today = new Date();
  const currentMonth = today.getMonth();
  const currentYear = today.getFullYear();
  const stateYear = (state as any)?.year;
  const initialYear = getInitialYear(currentYear, currentMonth);
  window.history.replaceState({}, document.title);

  const navigate = useNavigate();
  const { currentBroker } = useBroker();

  const [data, setData] = useState<any[]>();
  const [loading, setLoading] = useState(false);
  const [year, setYear] = useState(stateYear ?? initialYear);
  const [month, setMonth] = useState(0);
  const [darf, setDarf] = useState<{ id: string }>();

  const historicFeature = item.features[0];
  const payFeature = item.features[1];

  const backend = item.path === "mynt/cripto-historic" ? apis : apis;
  // const backend =
  //   item.path === "mynt/bolsa-historic"
  //     ? apiBolsa
  //     : item.path === "mynt/exterior-historic"
  //     ? apiExterior
  //     : item.path === "mynt/autonomos-historico"
  //     ? rendimentosBackend
  //     : item.path === "mynt/cripto-historic"
  //     ? apiCrypto
  //     : apis;

  const DarfPage = currentBroker.pages
    .reduce((acc, cur) => [...acc, ...(cur.subPages ?? [cur])], [] as Page[])
    .find((page) =>
      item.settings?.darfComponent
        ? page.component === item.settings?.darfComponent
        : page.component === "Darf" ||
          page.component === "DarfBolsa" ||
          page.component === "Rendimentos"
    )!;

  const yearOptions = Array.from(
    Array(initialYear - currentBroker.initialYear + 1)
  )
    .map((y, i) => ({
      value: currentBroker.initialYear + i,
      label: `${currentBroker.initialYear + i}`,
    }))
    .reverse();

  const getHistoric = useCallback(() => {
    setLoading(true);
    backend
      .get(historicFeature.apiUrl, {
        params: { year },
      })
      .then((response) => setData((response.data ?? []).reverse()))
      .catch(() => message.error(errorMessage))
      .finally(() => setLoading(false));
  }, [historicFeature, year, backend]);

  const pay = useCallback(
    (payed: boolean, item: any) => {
      setLoading(true);
      backend
        .put(`${payFeature.apiUrl}/${item._id}`, { payed })
        .then(() => {
          getHistoric();
          setData((data) =>
            (data || []).map((darf) => ({
              ...darf,
              payed: item._id === darf._id ? payed : darf.payed,
            }))
          );
        })
        .catch(() => message.error(errorMessage))
        .finally(() => setLoading(false));
    },
    [payFeature, getHistoric, backend]
  );

  const downloadAction = (record: any, index: number) => {
    backend.get(`${item.features[2].apiUrl}/${record._id}`).then((res) => {
      if (res.data.url) {
        download(res.data.url);
      }
    });
  };

  const editAction = (record: any, index: number) => {
    const { month, year } = record;
    const subPath =
      item.path === "velotax/autonomos-historico" ? "autonomos" : "darf";
    navigate(item.settings?.darfUrl ?? `/${currentBroker.path}/${subPath}`, {
      state: { month: month, year },
    });
  };

  const viewAction = (record: any, index: number) => {
    const { _id } = record;
    const { month } = record;
    setMonth(month);
    setDarf({ id: _id });
  };

  const payAction = (value: boolean, record: any) => {
    pay(value, record);
  };

  const handleCloseDarfModal = () => {
    setDarf(undefined);
  };

  const onChangeYear = (value: string) => {
    setYear(parseInt(value));
  };

  const handleValue = (data: any) => {
    return (
      // (data.totalImpostoDevido || 0) +
      (data.impostoDevido || 0) + (data.impostoAcumulado || 0)
    );
  };

  useEffect(() => {
    getHistoric();
  }, [getHistoric, year]);

  return (
    <Container>
      <Typography.Title level={1}>Histórico de impostos</Typography.Title>
      <Typography.Title level={2}>
        Mantenha atualizado o controle dos seus pagamentos de DARF.
      </Typography.Title>
      <Typography.Title level={2} className="info">
        <InfoCircleOutlined />A confirmação do seu pagamento será automática
        somente quando o pagamento do DARF for realizado por PIX ou cartão.
      </Typography.Title>
      <Content>
        <Typography.Paragraph>Selecione o ano:</Typography.Paragraph>
        <Select
          size="large"
          options={yearOptions}
          value={year.toString()}
          onChange={onChangeYear}
          placeholder="Selecione o ano"
          style={{ minWidth: "150px" }}
          getPopupContainer={(target) => target.parentElement}
        />

        <List
          loading={loading}
          dataSource={
            data?.filter(
              (item) =>
                !(item.year === currentYear && item.month > currentMonth)
            ) ?? []
          }
          itemLayout="horizontal"
          renderItem={(item, index) => (
            <List.Item
              actions={[
                item.fullPath && (
                  <>
                    Pago?
                    <Button
                      type="text"
                      size="large"
                      onClick={() => payAction(!item.payed, item)}
                      icon={
                        item.payed ? (
                          <CheckCircleOutlined className="success-icon" />
                        ) : (
                          <CloseCircleOutlined className="error-icon" />
                        )
                      }
                    />
                  </>
                ),
                item._id && (
                  <Button
                    type="text"
                    size="large"
                    onClick={() => viewAction(item, index)}
                    icon={<BsEye size={16} />}
                  />
                ),
                item.fullPath && (
                  <Button
                    type="text"
                    size="large"
                    onClick={() => downloadAction(item, index)}
                    icon={<BsDownload size={16} />}
                  />
                ),
                <Button
                  type="text"
                  size="large"
                  onClick={() => editAction(item, index)}
                  icon={<FaRegEdit size={16} />}
                />,
              ]}
            >
              <Skeleton title={false} loading={item.loading} active>
                <List.Item.Meta
                  title={`${monthsExtended[item.month - 1]} de ${item.year}`}
                  description={
                    <Space direction="vertical">
                      {formatCurrency(handleValue(item))}
                      <div className={"div-historic-month-status"}>
                        <Typography.Text>Situação:</Typography.Text>
                        <span
                          className={clsx(
                            "historic-month-status",
                            YearResumeStatusCSSClassEnum[
                              item.status as keyof typeof YearResumeStatusCSSClassEnum
                            ]
                          )}
                        >
                          {
                            YearResumeStatusTextEnum[
                              item.status as keyof typeof YearResumeStatusTextEnum
                            ]
                          }
                        </span>
                      </div>
                    </Space>
                  }
                />
              </Skeleton>
            </List.Item>
          )}
        />
      </Content>
      <Modal
        closable
        title={null}
        footer={null}
        visible={!!darf}
        onCancel={handleCloseDarfModal}
        bodyStyle={{ paddingBottom: "32px" }}
      >
        {DarfPage.component === "DarfBolsa" ? (
          <DarfBolsa view={true} darf={darf} item={DarfPage} />
        ) : DarfPage.component === "DarfExterior" ? (
          <DarfExterior view={true} darf={darf} item={DarfPage} />
        ) : DarfPage.component === "Darf" ? (
          <Darf view={true} darf={darf} item={DarfPage} />
        ) : (
          <Rendimentos
            view={true}
            darf={darf}
            item={DarfPage}
            yearView={year}
            monthView={month - 1}
          />
        )}
      </Modal>
    </Container>
  );
};
